import { makeStyles } from '@material-ui/styles';
import Link from 'next/link';
import theme from '../../theme';
import Image from 'next/image';
import { TrackingContext } from '../../providers/tracking';
import { useContext } from 'react';
import { isCanvasProduct } from '@tools/canvas-functionality';
import ProductThumbnail from '@components/products/thumbnail';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			paddingTop: 15,
			width: '100%',
			[theme.breakpoints.down('sm')]: {
				'&.last': {
					marginBottom: 10
				}
			}
		},
		label: {
			fontSize: 12,
			fontWeight: 300,
			letterSpacing: 0.5,
			color: '#000000',
			textTransform: 'uppercase'
		},
		list: {
			display: 'flex',
			flexWrap: 'wrap',
			padding: 0,
			marginTop: 8,
			marginBottom: 0,
			listStyle: 'none',
			[theme.breakpoints.up('md')]: {
				marginTop: 10
			}
		},
		option: {
			display: 'flex',
			margin: '0px 14px 14px 0',
			fontSize: 12,
			fontWeight: 400,
			color: '#888',
			lineHeight: '12px',
			backgroundColor: theme.palette.primary.background,
			cursor: 'pointer',
			border: '1px solid #888',
			overflow: 'hidden',
			'& a': {
				display: 'flex',
				padding: '6px 8px',
				color: '#000',
				height: '100%',
				width: '100%',
			},
			'&.active': {
				fontWeight: 600,
				border: '1px solid #444',
				boxShadow: '0px 0px 0px 1px #444',
				color: '#444',
				transition: '0.12s',
				'& img': {
					margin: -1
				}
			},
			'&.image': {
				width: 'calc((100% / 5) - 12.8px)',
				aspectRatio: '4 / 5',
				'& a': {
					padding: 0,
					aspectRatio: '4 / 5',
				},
				[theme.breakpoints.up('md')]: {
					width: 'calc((100% / 6) - 13.333px)'
				}
			},
			'&.disabled': {
				backgroundColor: '#ccc',
				color: theme.palette.primary.background,
				opacity: 0.25,
			},
			'&.not-available': {
				display: 'none',
				backgroundColor: '#ccc',
				color: theme.palette.primary.background,
				pointerEvents: 'none'
			},
			'&.soldOut': {
				background:
					'linear-gradient(to bottom right, transparent calc(50% - 0.5px), currentColor 50%, transparent calc(50% + 0.5px)) transparent'
			},
			[theme.breakpoints.up('md')]: {
				margin: '0px 16px 16px 0',
				'& a': {
					padding: '10px 12px'
				}
			}
		},
		variantImage: {
			width: '100%'
		}
	}),
	{ name: 'ProductConfigurator' }
);

export default function ProductConfigurator({ product, configurator }) {
	const classes = useStyles();
	const { actions: trackingActions } = useContext(TrackingContext);
	const variantData = product.translated?.customFields?.variant_data || [];
	const splitGroupIds = product.translated?.customFields?.variant_split_group_ids || null;
	const isCanvas = isCanvasProduct(product);

	function getOptionProduct(groupId, optionId) {
		let selection = {};
		let availableOptions = configurator
			.map(group => {
				return group.options.map(item => item.id);
			})
			.flat();

		selection[groupId] = optionId;

		configurator
			.filter(group => {
				return group.id !== groupId;
			})
			.forEach(group => {
				let tempSelection = group.options.find(option => {
					return product.optionIds?.includes(option.id);
				});

				if (tempSelection && tempSelection.id) {
					selection[group.id] = tempSelection.id;
				}
			});

		const compare = Object.values(selection)
			.sort()
			.join('-');

		let newProduct = variantData.find(item => {
			let compareValue = item.optionIds
				?.filter(item => availableOptions?.includes(item))
				.sort()
				.join('-');

			return compareValue === compare;
		});

		if (!newProduct) {
			const backupProduct = variantData.find((item) => {
				return item.optionIds.includes(optionId);
			});

			if (backupProduct) {
				newProduct = backupProduct;
			} else {
				return '';
			}
		}

		let url = '/detail/' + newProduct.id;
		if (newProduct.seoUrls && newProduct.seoUrls.length > 0) {
			url = '/' + newProduct.seoUrls[0].seoPathInfo;
		}

		return {
			url,
			product: newProduct,
		};
	}

	function isOptionSoldOut(groupId, itemId) {
		let selection = {};
		let availableOptions = configurator
			.map(group => {
				return group.options.map(item => item.id);
			})
			.flat();

		selection[groupId] = itemId;

		configurator
			.filter(group => {
				return group.id !== groupId;
			})
			.forEach(group => {
				let tempSelection = group.options.find(option => {
					return product.optionIds?.includes(option.id);
				});

				if (tempSelection && tempSelection.id) {
					selection[group.id] = tempSelection.id;
				}
			});

		const compare = Object.values(selection)
			.sort()
			.join('-');

		const itemProduct = variantData.find(item => {
			let compareValue = item.optionIds
				?.filter(item => availableOptions?.includes(item))
				.sort()
				.join('-');

			return compareValue === compare;
		});

		return itemProduct ? !itemProduct.available : true;
	}

	function getVariantImage(optionId, groupId) {
		let image = false;

		let selectedOptions = getSelectedOptions();

		let otherOptionIds = [];
		selectedOptions.forEach(option => {
			if (option && option.groupId != groupId) {
				otherOptionIds.push(option.optionId);
			}
		});

		variantData.forEach(variant => {
			let correctVariant = false;
			if (variant?.optionIds?.indexOf(optionId) >= 0) {
				let otherOptionsMatch = true;
				otherOptionIds.forEach(optionId => {
					if (variant?.optionIds?.indexOf(optionId) < 0) {
						otherOptionsMatch = false;
					}
				});

				if (otherOptionsMatch) {
					correctVariant = true;
				}
			}

			if (correctVariant) {
				image = variant.cover || false;
			}
		});


		if (!image) {
			const result = variantData.find((product) => {
				return product?.optionIds?.includes(optionId);
			});

			image = result?.cover;
		}


		if (!image) {
			image = {
				url: '/images/placeholder/product.jpg',
				metaData: {
					width: 1020,
					height: 800
				}
			};
		}

		return image;
	}

	function renderOptions(options, id, groupIndex) {
		if (!variantData) {
			return [];
		}

		let validOptions = options.filter(option => {
			let validOption = false;
			variantData.forEach(variant => {
				if (variant?.optionIds?.indexOf(option.id) >= 0) {
					validOption = true;
				}
			});

			return validOption;
		});

		return validOptions.sort((a, b) => {
			if(a.position > b.position) {
				return 1;
			} else if (a.position < b.position) {
				return -1;
			} else {
				return 0;
			}
		}).map(item => {
			const optionProduct = getOptionProduct(item.groupId, item.id);
			let isImageAttribute = !!(splitGroupIds && splitGroupIds.indexOf(item.groupId) >= 0);

			return (
				<li
					className={[
						classes.option,
						product.optionIds?.includes(item.id) ? 'active' : '',
						isImageAttribute ? 'image' : '',
						!optionProduct.url.length ? 'disabled' : '',
						isOptionSoldOut(item.groupId, item.id) ? 'soldOut' : ''
					].join(' ')}
					aria-disabled={!optionProduct.url.length}
					key={[item.groupId, item.id].join('-')}
				>
					<Link href={optionProduct.url} scroll={false} replace={true}>
						<a
							style={{ textDecoration: 'none' }}
							onClick={() =>
								trackingActions.click({ type: 'product_variant', text: item.name, identifier: optionProduct.url })
							}
						>
							{isImageAttribute ? (
								<ProductThumbnail product={optionProduct.product} imageType={'standard'} />
							) : (
								item?.translated?.name || item.name
							)}
						</a>
					</Link>
				</li>
			);
		});
	}

	function getSelectedOptions() {
		return configurator.map(group => {
			let option = group.options.filter(option => {
				return product.optionIds?.includes(option.id);
			});

			if (option.length > 0) {
				return {
					optionId: option[0].id,
					groupId: option[0].groupId
				};
			}
		});
	}

	function getCurrentOptionLabel(groupId, options) {
		let selectedOption = options.filter(option => {
			return product.optionIds?.includes(option.id);
		});

		if (selectedOption.length > 0) {
			selectedOption = selectedOption[0];
		}

		return selectedOption?.translated?.name;
	}

	const options = configurator.map((group, index) => {
		if (group.options && group.options.length <= 0) {
			return;
		}

		return (
			<div
				className={[classes.wrapper, configurator.length === index + 1 ? 'last' : '', isCanvas ? 'is-canvas' : ''].join(' ')}
				key={group.id}
			>
				<label className={classes.label}>
					{group.translated.name}: {getCurrentOptionLabel(group.id, group.options)}
				</label>
				<ul className={classes.list}>{renderOptions(group.options, group.id, index)}</ul>
			</div>
		);
	});

	return <>{options}</>;
}
